import { useState, useEffect } from 'react'
import { Wifi, Copy, Clock, Server, Activity, CheckCircle, AlertCircle, Download, Upload, Globe } from 'lucide-react'
import { networkAPI } from '@/lib/api'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function NetworkToolsPage() {
  const [networkInfo, setNetworkInfo] = useState(null)
  const [ping, setPing] = useState(null)
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState(null)

  // Speed test states
  const [lanSpeedTest, setLanSpeedTest] = useState({
    download: null,
    upload: null,
    testing: false
  })
  const [internetTest, setInternetTest] = useState({
    results: null,
    testing: false
  })

  useEffect(() => {
    fetchNetworkInfo()
    testPing()
  }, [])

  const fetchNetworkInfo = async () => {
    try {
      setLoading(true)
      const response = await networkAPI.getNetworkInfo()
      setNetworkInfo(response.data)
    } catch (err) {
      setError('Failed to fetch network information')
    } finally {
      setLoading(false)
    }
  }

  const testPing = async () => {
    try {
      setTesting(true)
      const start = performance.now()
      await networkAPI.ping()
      const end = performance.now()
      const latency = Math.round(end - start)
      setPing(latency)
    } catch (err) {
      setPing(null)
    } finally {
      setTesting(false)
    }
  }

  const testLanDownload = async () => {
    try {
      setLanSpeedTest(prev => ({ ...prev, testing: true }))
      const sizeMB = 10 // Test with 10MB
      const startTime = performance.now()

      const response = await networkAPI.speedTestDownload(sizeMB)
      const endTime = performance.now()

      const durationSeconds = (endTime - startTime) / 1000
      const speedMbps = ((sizeMB * 8) / durationSeconds).toFixed(2)

      setLanSpeedTest(prev => ({
        ...prev,
        download: { speedMbps, duration: durationSeconds.toFixed(2), sizeMB }
      }))
    } catch (err) {
      console.error('Download test failed:', err)
      alert('Download test failed')
    } finally {
      setLanSpeedTest(prev => ({ ...prev, testing: false }))
    }
  }

  const testLanUpload = async () => {
    try {
      setLanSpeedTest(prev => ({ ...prev, testing: true }))
      const sizeMB = 5 // Test with 5MB
      const sizeBytes = sizeMB * 1024 * 1024

      // Generate random data
      const data = new Uint8Array(sizeBytes)
      crypto.getRandomValues(data)

      const startTime = performance.now()
      const response = await networkAPI.speedTestUpload(data)
      const endTime = performance.now()

      const durationSeconds = (endTime - startTime) / 1000
      const speedMbps = response.data.speedMbps || ((sizeMB * 8) / durationSeconds).toFixed(2)

      setLanSpeedTest(prev => ({
        ...prev,
        upload: { speedMbps, duration: durationSeconds.toFixed(2), sizeMB }
      }))
    } catch (err) {
      console.error('Upload test failed:', err)
      alert('Upload test failed')
    } finally {
      setLanSpeedTest(prev => ({ ...prev, testing: false }))
    }
  }

  const testInternetConnectivity = async () => {
    try {
      setInternetTest({ testing: true, results: null })
      const response = await networkAPI.testInternetSpeed()
      setInternetTest({ testing: false, results: response.data })
    } catch (err) {
      console.error('Internet test failed:', err)
      setInternetTest({ testing: false, results: null })
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert('Copied to clipboard!')
  }

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${days}d ${hours}h ${minutes}m`
  }

  const getPingQuality = (latency) => {
    if (latency < 50) return { label: 'Excellent', color: 'text-neon-green' }
    if (latency < 100) return { label: 'Good', color: 'text-neon-blue' }
    if (latency < 200) return { label: 'Fair', color: 'text-yellow-500' }
    return { label: 'Poor', color: 'text-red-500' }
  }

  const getSpeedQuality = (speedMbps) => {
    const speed = parseFloat(speedMbps)
    if (speed >= 100) return { label: 'Excellent', color: 'text-neon-green' }
    if (speed >= 50) return { label: 'Good', color: 'text-neon-blue' }
    if (speed >= 10) return { label: 'Fair', color: 'text-yellow-500' }
    return { label: 'Poor', color: 'text-red-500' }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Network Tools</h1>
        <p className="text-muted-foreground mt-1">
          Server network information and diagnostics
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Server IP Addresses */}
        <Card className="border-neon-green/30">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Server className="h-5 w-5 text-neon-green" />
              <span>Server Addresses</span>
            </CardTitle>
            <CardDescription>LAN IP addresses to connect from other devices</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading...</p>
            ) : networkInfo?.addresses?.length > 0 ? (
              networkInfo.addresses.map((addr, index) => (
                <div key={index} className="p-3 rounded-md border border-neon-green/20 bg-neon-green/5">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs">{addr.name}</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(addr.address)}
                      className="h-7 px-2 text-neon-green hover:bg-neon-green/10"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <p className="text-lg font-mono font-bold text-neon-green">{addr.address}</p>
                  <p className="text-xs text-muted-foreground mt-1">Netmask: {addr.netmask}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No network addresses found</p>
            )}
          </CardContent>
        </Card>

        {/* Connection Test */}
        <Card className="border-neon-blue/30">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-neon-blue" />
              <span>Connection Test</span>
            </CardTitle>
            <CardDescription>Test your connection to the server</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-md border border-neon-blue/20 bg-neon-blue/5 text-center">
              {ping !== null ? (
                <>
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <CheckCircle className={`h-5 w-5 ${getPingQuality(ping).color}`} />
                    <span className={`text-sm ${getPingQuality(ping).color}`}>
                      {getPingQuality(ping).label}
                    </span>
                  </div>
                  <p className="text-3xl font-bold text-neon-blue">{ping}ms</p>
                  <p className="text-xs text-muted-foreground mt-1">Round-trip time</p>
                </>
              ) : (
                <p className="text-muted-foreground">Click test to measure latency</p>
              )}
            </div>
            <Button
              onClick={testPing}
              disabled={testing}
              className="w-full bg-neon-blue/10 hover:bg-neon-blue/20 text-neon-blue border border-neon-blue/30"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
          </CardContent>
        </Card>

        {/* LAN Speed Test */}
        <Card className="border-neon-purple/30">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-neon-purple" />
              <span>LAN Speed Test</span>
            </CardTitle>
            <CardDescription>Test upload/download speed on local network</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Download Speed */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center">
                  <Download className="h-4 w-4 mr-2 text-neon-purple" />
                  Download Speed
                </span>
                {lanSpeedTest.download && (
                  <Badge variant="outline" className={getSpeedQuality(lanSpeedTest.download.speedMbps).color}>
                    {lanSpeedTest.download.speedMbps} Mbps
                  </Badge>
                )}
              </div>
              <Button
                onClick={testLanDownload}
                disabled={lanSpeedTest.testing}
                size="sm"
                variant="outline"
                className="w-full"
              >
                {lanSpeedTest.testing ? 'Testing...' : 'Test Download (10MB)'}
              </Button>
            </div>

            {/* Upload Speed */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center">
                  <Upload className="h-4 w-4 mr-2 text-neon-purple" />
                  Upload Speed
                </span>
                {lanSpeedTest.upload && (
                  <Badge variant="outline" className={getSpeedQuality(lanSpeedTest.upload.speedMbps).color}>
                    {lanSpeedTest.upload.speedMbps} Mbps
                  </Badge>
                )}
              </div>
              <Button
                onClick={testLanUpload}
                disabled={lanSpeedTest.testing}
                size="sm"
                variant="outline"
                className="w-full"
              >
                {lanSpeedTest.testing ? 'Testing...' : 'Test Upload (5MB)'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Internet Connectivity Test */}
        <Card className="border-neon-green/30">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-neon-green" />
              <span>Internet Connectivity</span>
            </CardTitle>
            <CardDescription>Test server's internet connection</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {internetTest.results ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium">
                    {internetTest.results.summary.internetConnected ? (
                      <span className="text-neon-green flex items-center">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Connected
                      </span>
                    ) : (
                      <span className="text-red-500 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        No Connection
                      </span>
                    )}
                  </span>
                  {internetTest.results.summary.averageLatency && (
                    <Badge variant="outline" className="text-neon-green">
                      Avg: {internetTest.results.summary.averageLatency}ms
                    </Badge>
                  )}
                </div>

                <div className="space-y-1">
                  {internetTest.results.results.map((result, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                      <span className="text-muted-foreground">{result.name}</span>
                      {result.reachable ? (
                        <Badge variant="outline" className="text-xs text-neon-green">
                          {result.latency.toFixed(0)}ms
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-red-500">
                          Failed
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Click test to check internet connectivity
              </p>
            )}

            <Button
              onClick={testInternetConnectivity}
              disabled={internetTest.testing}
              className="w-full bg-neon-green/10 hover:bg-neon-green/20 text-neon-green border border-neon-green/30"
            >
              {internetTest.testing ? 'Testing...' : 'Test Internet Connection'}
            </Button>
          </CardContent>
        </Card>

        {/* Server Info */}
        <Card className="border-neon-purple/30">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Wifi className="h-5 w-5 text-neon-purple" />
              <span>Server Information</span>
            </CardTitle>
            <CardDescription>System details and status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <p className="text-muted-foreground text-sm">Loading...</p>
            ) : networkInfo ? (
              <>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Hostname</span>
                  <span className="text-sm font-medium">{networkInfo.hostname}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Platform</span>
                  <Badge variant="outline">{networkInfo.platform}</Badge>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    Uptime
                  </span>
                  <span className="text-sm font-medium text-neon-purple">
                    {formatUptime(networkInfo.uptime)}
                  </span>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-neon-green/30">
          <CardHeader>
            <CardTitle>Quick Setup Guide</CardTitle>
            <CardDescription>Connect other devices to this server</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <p className="font-medium mb-1">1. Find your server IP</p>
              <p className="text-muted-foreground">Use any IP address listed above (usually the first one)</p>
            </div>
            <div>
              <p className="font-medium mb-1">2. Connect from another device</p>
              <p className="text-muted-foreground">
                Open browser and go to: <code className="px-1 py-0.5 bg-muted rounded text-neon-green">http://SERVER_IP</code>
              </p>
            </div>
            <div>
              <p className="font-medium mb-1">3. Check firewall</p>
              <p className="text-muted-foreground">Ensure ports 80 and 3000 are allowed on your firewall</p>
            </div>
            <Button
              size="sm"
              onClick={fetchNetworkInfo}
              variant="outline"
              className="w-full mt-2"
            >
              Refresh Network Info
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
